/**
 * Overview Tab - Presence statistics overview
 */

import { useEffect } from 'react';
import { IconUsers, IconCircleCheck, IconClock, IconAlertCircle, IconRefresh } from '@tabler/icons-react';
import { Card, Text, Grid, Group, Button, Stack } from '../../../../components/ui';
import { SlideIn } from '../../../../components/animations';
import { usePresenceManager } from '../../../../hooks/usePresenceManager';

export function OverviewTab() {
  const { presenceStats, refreshStats, isAdmin, isConnected, manager } = usePresenceManager();

  // Debug logging
  useEffect(() => {
    console.log('[OverviewTab] Debug:', { isAdmin, hasStats: !!presenceStats, isConnected, hasManager: !!manager });
  }, [isAdmin, presenceStats, isConnected, manager]);

  if (!isAdmin) {
    return (
      <Stack gap="md" align="center" p="xl">
        <IconAlertCircle size={48} color="var(--mantine-color-orange-6)" />
        <Text size="lg" fw={600}>
          Admin Access Required
        </Text>
        <Text size="sm" c="dimmed" ta="center">
          You need Admin role in your JWT token to view presence statistics.
          <br />
          Current admin status: {isAdmin ? 'Yes' : 'No'}
        </Text>
        <Text size="xs" c="dimmed" ta="center">
          Check browser console for details.
        </Text>
      </Stack>
    );
  }

  if (!presenceStats) {
    return (
      <Stack gap="md" align="center" p="xl">
        <IconAlertCircle size={48} color="var(--mantine-color-blue-6)" />
        <Text size="lg" fw={600}>
          Loading Statistics...
        </Text>
        <Text size="sm" c="dimmed" ta="center">
          Admin access confirmed. Fetching presence statistics.
        </Text>
        <Button onClick={refreshStats} variant="light">
          Retry Loading Stats
        </Button>
      </Stack>
    );
  }

  return (
    <Stack gap="lg">
      {/* Stats Cards */}
      <Grid gutter="md">
        {/* Total Users Card */}
        <Grid.Col span={{ base: 12, sm: 6, md: 3 }}>
          <Card variant="gradient" gradientVariant="primary">
            <SlideIn direction="up" delay={0}>
              <Stack gap="xs">
                <Group gap="xs">
                  <IconUsers size={16} color="rgba(255, 255, 255, 0.9)" />
                  <Text size="sm" c="white" style={{ opacity: 0.9 }}>
                    Total Users
                  </Text>
                </Group>
                <Text size="xl" fw={700} c="white">
                  {presenceStats.totalUsers || 0}
                </Text>
              </Stack>
            </SlideIn>
          </Card>
        </Grid.Col>

        {/* Online Users Card */}
        <Grid.Col span={{ base: 12, sm: 6, md: 3 }}>
          <Card variant="gradient" gradientVariant="success">
            <SlideIn direction="up" delay={100}>
              <Stack gap="xs">
                <Group gap="xs">
                  <IconCircleCheck size={16} color="rgba(255, 255, 255, 0.9)" />
                  <Text size="sm" c="white" style={{ opacity: 0.9 }}>
                    Online
                  </Text>
                </Group>
                <Text size="xl" fw={700} c="white">
                  {presenceStats.onlineUsers || 0}
                </Text>
              </Stack>
            </SlideIn>
          </Card>
        </Grid.Col>

        {/* Away Users Card */}
        <Grid.Col span={{ base: 12, sm: 6, md: 3 }}>
          <Card variant="gradient" gradientVariant="warning">
            <SlideIn direction="up" delay={200}>
              <Stack gap="xs">
                <Group gap="xs">
                  <IconClock size={16} color="rgba(255, 255, 255, 0.9)" />
                  <Text size="sm" c="white" style={{ opacity: 0.9 }}>
                    Away
                  </Text>
                </Group>
                <Text size="xl" fw={700} c="white">
                  {presenceStats.awayUsers || 0}
                </Text>
              </Stack>
            </SlideIn>
          </Card>
        </Grid.Col>

        {/* Busy Users Card */}
        <Grid.Col span={{ base: 12, sm: 6, md: 3 }}>
          <Card variant="gradient" gradientVariant="danger">
            <SlideIn direction="up" delay={300}>
              <Stack gap="xs">
                <Group gap="xs">
                  <IconAlertCircle size={16} color="rgba(255, 255, 255, 0.9)" />
                  <Text size="sm" c="white" style={{ opacity: 0.9 }}>
                    Busy
                  </Text>
                </Group>
                <Text size="xl" fw={700} c="white">
                  {presenceStats.busyUsers || 0}
                </Text>
              </Stack>
            </SlideIn>
          </Card>
        </Grid.Col>
      </Grid>

      {/* Refresh Button */}
      <Group justify="flex-end">
        <Button
          leftSection={<IconRefresh size={16} />}
          onClick={refreshStats}
          variant="light"
        >
          Refresh Stats
        </Button>
      </Group>
    </Stack>
  );
}
